import { supabase } from "@/integrations/supabase/client";

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  user_name: string;
  avatar_url?: string;
  agent_id: string;
  agent_name: string;
  simulation_id: string;
  best_score: number;
  average_score: number;
  total_sessions: number;
  last_session_date: string;
  company_id?: string;
  company_name?: string;
  // New coaching fields
  coaching_sessions?: number;
  avg_readiness?: number;
  total_cpd_points?: number;
}

export interface UserRankInfo {
  rank: number;
  total_users: number;
  percentile: number;
  best_score: number;
  average_score: number;
  total_sessions: number;
  coaching_sessions?: number;
  total_cpd_points?: number;
}

export interface FilterOptions {
  timePeriod?: 'all' | 'month' | 'week' | 'today';
  agentId?: string;
  companyId?: string;
  limit?: number;
  activityType?: 'all' | 'simulations' | 'coaching';
  tenantId?: string; // For white-label tenant isolation
}

interface LeaderboardQueryOptions {
  includeCpd?: boolean;
}

// Master tenant ID - admins of this tenant see all data
const MASTER_TENANT_ID = '00000000-0000-0000-0000-000000000001';

export async function fetchLeaderboard(
  filters?: FilterOptions,
  userCompanyId?: string,
  isAdmin?: boolean,
  tenantId?: string,
  { includeCpd = true }: LeaderboardQueryOptions = {},
): Promise<LeaderboardEntry[]> {
  // Check if this is a white-label tenant (not master MEDD)
  const isWhiteLabelTenant = tenantId && tenantId !== MASTER_TENANT_ID;

  let query = supabase
    .from('leaderboard_all_time')
    .select('*');

  // 🔒 SECURITY: White-label tenant admins only see their own tenant's data
  if (isWhiteLabelTenant) {
    // For white-label tenants, we need to filter by tenant_id
    // Since leaderboard_all_time may not have tenant_id, we filter via company_id
    // Get companies belonging to this tenant first
    const { data: tenantCompanies } = await supabase
      .from('companies')
      .select('id')
      .eq('tenant_id', tenantId);
    
    const tenantCompanyIds = tenantCompanies?.map(c => c.id) || [];
    
    if (tenantCompanyIds.length === 0) {
      // No companies for this tenant - return empty
      return [];
    }
    
    // Filter leaderboard to only show users from tenant's companies
    query = query.in('company_id', tenantCompanyIds);
  } else if (!isAdmin && userCompanyId) {
    // Non-admin with company sees only their company
    query = query.eq('company_id', userCompanyId);
  } else if (!isAdmin && !userCompanyId) {
    // User has no company - return empty array
    return [];
  }

  // For master tenant admins, only apply company filter if explicitly selected
  if (!isWhiteLabelTenant && isAdmin && filters?.companyId) {
    query = query.eq('company_id', filters.companyId);
  }

  // Apply time period filter
  if (filters?.timePeriod && filters.timePeriod !== 'all') {
    const now = new Date();
    let dateThreshold: string;

    switch (filters.timePeriod) {
      case 'today':
        dateThreshold = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case 'week':
        const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
        dateThreshold = new Date(weekStart.setHours(0, 0, 0, 0)).toISOString();
        break;
      case 'month':
        dateThreshold = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        break;
      default:
        dateThreshold = '';
    }

    if (dateThreshold) {
      query = query.gte('last_session_date', dateThreshold);
    }
  }

  // Apply agent filter
  if (filters?.agentId) {
    query = query.eq('agent_id', filters.agentId);
  }

  // Apply limit
  const limit = filters?.limit || 20;
  query = query.limit(limit);

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }

  // Find entries with missing user_name and fetch from profiles
  const missingNameUserIds = (data || [])
    .filter(d => !d.user_name)
    .map(d => d.user_id)
    .filter(Boolean);

  let profileMap: Record<string, string> = {};
  if (missingNameUserIds.length > 0) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, display_name, email')
      .in('id', missingNameUserIds);
    
    profiles?.forEach(p => {
      profileMap[p.id] = p.display_name || p.email?.split('@')[0] || 'Unknown';
    });
  }

  // Apply fallback names to data
  const enrichedBaseData = (data || []).map(entry => ({
    ...entry,
    user_name: entry.user_name || profileMap[entry.user_id] || 'Unknown User',
  }));

  // Enrich with coaching data and CPD points
  const enrichedData = await Promise.all(
    enrichedBaseData.map(async (entry) => {
      // Get coaching sessions count
      const { count: coachingCount } = await supabase
        .from('coaching_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', entry.user_id)
        .eq('status', 'completed');

      // Get average readiness score
      const { data: coachingData } = await supabase
        .from('coaching_sessions')
        .select('readiness_score')
        .eq('user_id', entry.user_id)
        .eq('status', 'completed')
        .not('readiness_score', 'is', null);

      const avgReadiness = coachingData?.length
        ? Math.round(coachingData.reduce((sum, s) => sum + (s.readiness_score || 0), 0) / coachingData.length)
        : 0;

      let totalCpd = 0;
      if (includeCpd) {
        const { data: cpdData } = await supabase
          .from('user_cpd_points')
          .select('points')
          .eq('user_id', entry.user_id);
        totalCpd = cpdData?.reduce((sum, row) => sum + (row.points || 0), 0) || 0;
      }

      return {
        ...entry,
        coaching_sessions: coachingCount || 0,
        avg_readiness: avgReadiness,
        total_cpd_points: totalCpd,
      };
    })
  );

  // Filter by activity type if specified
  let filteredData = enrichedData;
  if (filters?.activityType === 'simulations') {
    filteredData = enrichedData.filter(e => e.total_sessions > 0);
  } else if (filters?.activityType === 'coaching') {
    filteredData = enrichedData.filter(e => e.coaching_sessions > 0);
  }

  // Deduplicate by user_name (some users have multiple user_ids): keep entry with highest best_score
  const userBestMap = new Map<string, typeof filteredData[0]>();
  for (const entry of filteredData) {
    // Use normalized user_name as dedup key (handles same person with different user_ids)
    const dedupeKey = (entry.user_name || entry.user_id).toLowerCase().trim();
    const existing = userBestMap.get(dedupeKey);
    if (!existing || (entry.best_score || 0) > (existing.best_score || 0)) {
      userBestMap.set(dedupeKey, entry);
    }
  }

  // Sort by best_score descending and assign sequential ranks starting from 1
  const deduped = Array.from(userBestMap.values())
    .sort((a, b) => (b.best_score || 0) - (a.best_score || 0));
  
  // Combine agent names for users who had multiple entries
  for (const entry of deduped) {
    // Collect all agent names for this user from original data (match by name for cross-id dedup)
    const entryNameKey = (entry.user_name || '').toLowerCase().trim();
    const userEntries = filteredData.filter(e => 
      e.user_id === entry.user_id || 
      (e.user_name || '').toLowerCase().trim() === entryNameKey
    );
    const agentNames = [...new Set(userEntries.map(e => e.agent_name).filter(Boolean))];
    if (agentNames.length > 0) {
      entry.agent_name = agentNames.join(', ');
    } else {
      entry.agent_name = 'Multiple Agents';
    }
    // Sum up total sessions across all agent entries
    entry.total_sessions = userEntries.reduce((sum, e) => sum + (e.total_sessions || 0), 0);
  }

  // Re-assign ranks sequentially starting from 1
  deduped.forEach((entry, index) => {
    entry.rank = index + 1;
  });

  return deduped;
}

export async function fetchUserRank(
  userId: string,
  userCompanyId?: string,
  isAdmin?: boolean,
  { includeCpd = true }: LeaderboardQueryOptions = {},
): Promise<UserRankInfo | null> {
  try {
    // Get current user's auth_user_id for accurate querying
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) return null;

    // Get user's performance data - try by user_id first, then by auth_user_id
    let userPerf = null;
    
    // First try with user_id
    const { data: perfByUserId } = await supabase
      .from('team_performance_cache')
      .select('best_score, average_score, total_sessions, company_id')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (perfByUserId) {
      userPerf = perfByUserId;
    } else {
      // Try with auth_user_id
      const { data: perfByAuthId } = await supabase
        .from('team_performance_cache')
        .select('best_score, average_score, total_sessions, company_id')
        .eq('auth_user_id', authUser.id)
        .maybeSingle();
      
      userPerf = perfByAuthId;
    }

    // If still no performance data, try to calculate from evaluations using BOTH user_id and auth_user_id
    if (!userPerf) {
      const { data: evaluations } = await supabase
        .from('simulation_evaluations')
        .select('overall_score')
        .or(`user_id.eq.${userId},auth_user_id.eq.${authUser.id}`)
        .eq('is_submitted', true)
        .is('deleted_at', null)
        .not('overall_score', 'is', null);

      if (!evaluations || evaluations.length === 0) {
        return null;
      }

      const scores = evaluations.map(e => e.overall_score || 0);
      userPerf = {
        best_score: Math.max(...scores),
        average_score: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
        total_sessions: evaluations.length,
        company_id: userCompanyId || null,
      };
    }

    // Build company-scoped query for counting users
    let countQuery = supabase
      .from('team_performance_cache')
      .select('*', { count: 'exact', head: true })
      .not('best_score', 'is', null);

    // Apply company filter for non-admins
    if (!isAdmin && userCompanyId) {
      countQuery = countQuery.eq('company_id', userCompanyId);
    }

    // Get total number of users (company-scoped or global)
    const { count: totalUsers, error: countError } = await countQuery;

    if (countError) {
      console.error('Error counting users:', countError);
      return null;
    }

    // Build company-scoped query for ranking
    let rankQuery = supabase
      .from('team_performance_cache')
      .select('*', { count: 'exact', head: true })
      .gt('best_score', userPerf.best_score);

    // Apply company filter for non-admins
    if (!isAdmin && userCompanyId) {
      rankQuery = rankQuery.eq('company_id', userCompanyId);
    }

    // Get user's rank by counting how many users have a better score (company-scoped or global)
    const { count: betterScores, error: rankError } = await rankQuery;

    if (rankError) {
      console.error('Error calculating rank:', rankError);
      return null;
    }

    const rank = (betterScores || 0) + 1;
    const percentile = totalUsers ? ((1 - (rank / totalUsers)) * 100) : 0;

    // Fetch additional stats: coaching sessions and CPD points
    const { count: coachingCount } = await supabase
      .from('coaching_sessions')
      .select('*', { count: 'exact', head: true })
      .or(`user_id.eq.${userId},auth_user_id.eq.${authUser.id}`)
      .eq('status', 'completed');

    let totalCpd = 0;
    if (includeCpd) {
      const { data: cpdData } = await supabase
        .from('user_cpd_points')
        .select('points')
        .eq('user_id', userId);
      totalCpd = cpdData?.reduce((sum, row) => sum + (row.points || 0), 0) || 0;
    }

    return {
      rank,
      total_users: totalUsers || 0,
      percentile: Math.round(percentile * 10) / 10,
      best_score: userPerf.best_score,
      average_score: userPerf.average_score,
      total_sessions: userPerf.total_sessions,
      coaching_sessions: coachingCount || 0,
      total_cpd_points: totalCpd,
    };
  } catch (error) {
    console.error('Error in fetchUserRank:', error);
    return null;
  }
}

export async function fetchAgentsForFilter(): Promise<Array<{ id: string; name: string }>> {
  const { data, error } = await supabase
    .from('simulation_agents')
    .select('id, name')
    .eq('status', 'active')
    .order('name');

  if (error) {
    console.error('Error fetching agents:', error);
    throw error;
  }

  return data || [];
}

export async function fetchCompaniesForFilter(): Promise<Array<{ id: string; name: string }>> {
  const { data, error } = await supabase
    .from('companies')
    .select('id, name')
    .eq('is_active', true)
    .order('name');

  if (error) {
    console.error('Error fetching companies:', error);
    throw error;
  }

  return data || [];
}
